import { useReactTable } from '@tanstack/react-table';
import { FC } from 'react';
import { match } from 'ts-pattern';

import { AvatarWithInitials } from '@eluve/blocks';
import {
  ColDefBuilder,
  ExtendedSortingState,
  HStack,
  Heading,
  Paragraph,
  ServerDataTable,
  TableSearch,
  Tag,
  TagProps,
  VStack,
  useDataTableForQuery,
} from '@eluve/components';
import {
  CombinedTenantUsersInvitesBoolExp,
  CombinedTenantUsersInvitesOrderBy,
  DbEnums,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  useIsFeatureFlagEnabled,
} from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import { InviteRowActions } from './InviteRowActions';
import { InviteUserModal } from './InviteUserModal';
import { UserRowActions } from './UserRowActions';
import { searchTenantUsersInvitesQuery } from './operations';
import { CombinedRow, CombinedStatusTypesEnum, TableMeta } from './types';

const columns = new ColDefBuilder<CombinedRow>()
  .colDef({
    id: 'initials',
    header: '',
    cell: ({ row }) => {
      const hasFullName = row.original.fullName.length > 1;
      if (!hasFullName) {
        return null;
      }

      const { email, fullName, inviteId, userId } = row.original;
      return (
        <AvatarWithInitials
          name={fullName || email}
          id={userId ?? inviteId ?? undefined}
        />
      );
    },
  })
  .defaultSortable('fullName', {
    label: 'Full Name',
    cellRenderer: (row) => {
      const hasFullName = row.fullName.length > 1;
      return (
        <Paragraph color={hasFullName ? 'primary' : 'disabled'} size="md">
          {hasFullName ? row.fullName : '—'}
        </Paragraph>
      );
    },
  })
  .defaultSortable('email')
  .colDef({
    id: 'role',
    header: 'Role',
    cell: ({ row }) => (
      <Paragraph size="md">
        {row.original.role === 'tenant-admin' ? 'Admin' : 'Member'}
      </Paragraph>
    ),
  })
  .colDef({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const { color, label } = match<
        CombinedStatusTypesEnum,
        { label: string; color: TagProps['color'] }
      >(row.original.status)
        .with(DbEnums.OrganizationInviteStatus.PENDING, () => ({
          color: 'orange',
          label: 'Invite Pending',
        }))
        .with(DbEnums.OrganizationInviteStatus.REVOKED, () => ({
          color: 'negative',
          label: 'Invite Revoked',
        }))
        .with(DbEnums.OrganizationInviteStatus.EXPIRED, () => ({
          color: 'gray',
          label: 'Invite Expired',
        }))
        .otherwise(() => ({ color: 'lightgreen', label: 'Active' }));
      return <Tag color={color} label={label} size="s" />;
    },
  })
  .dateSortable('lastAccessedAt', 'Last Seen')
  .dateSortable('createdAt', 'Join/Invite Date')
  .colDef({
    accessorKey: 'actions',
    header: '',
    cell: ({ row, table }) => {
      if (row.original.userId) {
        return (
          <UserRowActions
            userId={row.original.userId}
            email={row.original.email}
            refetch={(table.options.meta as TableMeta).refetch}
            role={row.original.role}
          />
        );
      } else if (row.original.inviteId) {
        return (
          <InviteRowActions
            inviteId={row.original.inviteId}
            email={row.original.email}
            refetch={(table.options.meta as TableMeta).refetch}
            status={row.original.status}
          />
        );
      }
    },
  })
  .build();

const defaultSort: ExtendedSortingState<CombinedRow> = [
  { id: 'createdAt', desc: true },
];

export const UsersList: FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const invitesEnabled = useIsFeatureFlagEnabled('ORGANIZATION_USER_INVITES');

  const convertToRows = (
    results: ResultOf<typeof searchTenantUsersInvitesQuery>,
  ): CombinedRow[] => {
    let combinedTenantUsersInvites = results.combinedTenantUsersInvites;
    if (!invitesEnabled) {
      combinedTenantUsersInvites = combinedTenantUsersInvites.filter(
        (item) => item.inviteId == null,
      );
    }
    return combinedTenantUsersInvites.map((userOrInvite) => ({
      inviteId: userOrInvite.inviteId,
      userId: userOrInvite.userId,
      firstName: userOrInvite.firstName ?? '',
      lastName: userOrInvite.lastName ?? '',
      fullName: formatHumanName(
        userOrInvite.firstName ?? undefined,
        userOrInvite.lastName ?? undefined,
      ),
      email: userOrInvite.email ?? '',
      role: userOrInvite.role ?? 'tenant-member',
      status: userOrInvite.isExpired
        ? DbEnums.OrganizationInviteStatus.EXPIRED
        : (userOrInvite.status as CombinedStatusTypesEnum),
      lastAccessedAt: userOrInvite.lastAccessedAt,
      createdAt: userOrInvite.createdAt,
    }));
  };

  const {
    data,
    isPending,
    reactTableOptions,
    refetch,
    rows,
    search,
    setSearch,
  } = useDataTableForQuery({
    query: searchTenantUsersInvitesQuery,
    defaultSort,
    convertToRows,
    convertSearchParamsToVariables: ({ offset, limit, search, sorting }) => {
      const filterConditions: CombinedTenantUsersInvitesBoolExp[] = [
        { tenantId: { _eq: tenantId } },
      ];

      if (search) {
        filterConditions.push({
          _or: [
            { email: { _ilike: `%${search}%` } },
            { firstName: { _ilike: `%${search}%` } },
            { lastName: { _ilike: `%${search}%` } },
          ],
        });
      }

      return {
        filter: { _and: filterConditions },
        limit,
        offset,
        orderBy:
          sorting && sorting.length
            ? sorting.map(({ desc, id }) => ({
                ...match<keyof CombinedRow, CombinedTenantUsersInvitesOrderBy>(
                  id,
                )
                  .with('fullName', () => ({
                    firstName: desc ? 'DESC' : 'ASC',
                    lastName: desc ? 'DESC' : 'ASC',
                  }))
                  .otherwise(() => ({
                    [id]: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  })),
              }))
            : undefined,
      };
    },
  });

  const rowCount =
    data?.combinedTenantUsersInvitesAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    meta: { refetch },
    ...reactTableOptions,
  });

  return (
    <VStack className="py-8 sm:px-8" gap={8}>
      <HStack justify="between">
        <Heading size="2xl">Users</Heading>
        <FeatureFlaggedComponent flag="ORGANIZATION_USER_INVITES">
          <InviteUserModal onSuccess={() => refetch()} />
        </FeatureFlaggedComponent>
      </HStack>
      <VStack gap={0} className="rounded-md border border-border-primary">
        <TableSearch
          onChange={setSearch}
          placeholder="Search users by name or email..."
          value={search ?? ''}
        />
        <ServerDataTable<CombinedRow> isPending={isPending} table={table} />
      </VStack>
    </VStack>
  );
};
